import { metricRegistry } from './metrics';
import type { ChartConfig } from '../type/dynamicChart';

export type OptionFieldDef =
  | { kind: 'checkbox'; key: string; labelKey: string; default: boolean }
  | {
      kind: 'select';
      key: string;
      labelKey: string;
      options: { value: string; labelKey: string }[];
      default: string;
    }
  | { kind: 'number-input'; key: string; labelKey: string; min?: number; max?: number; default: number };

export type ChartTypeDef = {
  key: string;
  labelKey: string;
  supportedXKinds: ('numeric' | 'categorical')[];
  supportedYKinds: ('numeric' | 'categorical')[];
  optionFields: OptionFieldDef[];
  isConfigValid: (config: ChartConfig) => { valid: boolean; reason?: string };
};

function getMetricKind(metricKey: string): 'numeric' | 'categorical' | null {
  return metricRegistry.find((m) => m.key === metricKey)?.kind ?? null;
}

export const chartTypeRegistry: ChartTypeDef[] = [
  // ── Bar ─────────────────────────────────────────────────────────────────
  {
    key: 'bar',
    labelKey: 'analytics.dashboard.complexCharts.configuration.types.bar',
    supportedXKinds: ['categorical', 'numeric'],
    supportedYKinds: ['numeric'],
    optionFields: [
      {
        kind: 'select',
        key: 'sortOrder',
        labelKey: 'analytics.dashboard.complexCharts.configuration.sortOrder',
        options: [
          {
            value: 'desc',
            labelKey: 'analytics.dashboard.complexCharts.configuration.sortDescending',
          },
          {
            value: 'asc',
            labelKey: 'analytics.dashboard.complexCharts.configuration.sortAscending',
          },
          {
            value: 'alpha',
            labelKey: 'analytics.dashboard.complexCharts.configuration.sortAlphabetical',
          },
        ],
        default: 'desc',
      },
    ],
    isConfigValid: (config) => {
      const xKind = getMetricKind(config.xAxis.metric);
      if (xKind !== null && !['categorical', 'numeric'].includes(xKind)) {
        return { valid: false, reason: 'Bar chart X-axis must be categorical or numeric.' };
      }
      const yKind = getMetricKind(config.yAxis.metric);
      if (yKind !== 'numeric') {
        return { valid: false, reason: 'Bar chart Y-axis must be a numeric metric.' };
      }
      return { valid: true };
    },
  },

  // ── Pie ──────────────────────────────────────────────────────────────────
  {
    key: 'pie',
    labelKey: 'analytics.dashboard.complexCharts.configuration.types.pie',
    supportedXKinds: ['categorical'],
    supportedYKinds: ['numeric'],
    optionFields: [
      {
        kind: 'checkbox',
        key: 'showDataLabels',
        labelKey: 'analytics.dashboard.complexCharts.configuration.showDataLabels',
        default: true,
      },
    ],
    isConfigValid: (config) => {
      const xKind = getMetricKind(config.xAxis.metric);
      if (xKind !== 'categorical') {
        return { valid: false, reason: 'Pie chart X-axis (group-by) must be a categorical metric.' };
      }
      const yKind = getMetricKind(config.yAxis.metric);
      if (yKind !== 'numeric') {
        return { valid: false, reason: 'Pie chart Y-axis (value) must be a numeric metric.' };
      }
      return { valid: true };
    },
  },

  // ── Histogram ────────────────────────────────────────────────────────────
  {
    key: 'histogram',
    labelKey: 'analytics.dashboard.complexCharts.configuration.types.histogram',
    supportedXKinds: ['categorical'],
    supportedYKinds: ['numeric'],
    optionFields: [
      {
        kind: 'select',
        key: 'binCount',
        labelKey: 'analytics.dashboard.complexCharts.configuration.binCount',
        options: [
          { value: '5', labelKey: 'analytics.dashboard.complexCharts.configuration.bins5' },
          { value: '10', labelKey: 'analytics.dashboard.complexCharts.configuration.bins10' },
          { value: '20', labelKey: 'analytics.dashboard.complexCharts.configuration.bins20' },
          { value: '50', labelKey: 'analytics.dashboard.complexCharts.configuration.bins50' },
        ],
        default: '10',
      },
    ],
    isConfigValid: (config) => {
      const xKind = getMetricKind(config.xAxis.metric);
      if (xKind !== 'categorical') {
        return {
          valid: false,
          reason:
            'Histogram X-axis must be a categorical metric (e.g. tool ID, machine). Use a bar chart for numeric X-axis distributions.',
        };
      }
      const yKind = getMetricKind(config.yAxis.metric);
      if (yKind !== 'numeric') {
        return { valid: false, reason: 'Histogram Y-axis must be a numeric metric.' };
      }
      return { valid: true };
    },
  },

  // ── Radar ────────────────────────────────────────────────────────────────
  {
    key: 'radar',
    labelKey: 'analytics.dashboard.complexCharts.configuration.types.radar',
    supportedXKinds: ['categorical', 'numeric'],
    supportedYKinds: ['numeric'],
    optionFields: [
      {
        kind: 'select',
        key: 'radarMode',
        labelKey: 'analytics.dashboard.complexCharts.configuration.radarMode',
        options: [
          {
            value: 'categories',
            labelKey: 'analytics.dashboard.complexCharts.configuration.radarModes.categories',
          },
          {
            value: 'metrics',
            labelKey: 'analytics.dashboard.complexCharts.configuration.radarModes.metrics',
          },
        ],
        default: 'categories',
      },
      {
        kind: 'number-input',
        key: 'radarMaxItems',
        labelKey: 'analytics.dashboard.complexCharts.configuration.radarMaxItems',
        min: 3,
        max: 100,
        default: 8,
      },
    ],
    isConfigValid: (config) => {
      const yKind = getMetricKind(config.yAxis.metric);
      if (yKind !== 'numeric') {
        return { valid: false, reason: 'Radar chart Y-axis must be a numeric metric.' };
      }
      return { valid: true };
    },
  },

  // ── Scatter ──────────────────────────────────────────────────────────────
  {
    key: 'scatter',
    labelKey: 'analytics.dashboard.complexCharts.configuration.types.scatter',
    supportedXKinds: ['categorical', 'numeric'],
    supportedYKinds: ['numeric'],
    optionFields: [
      {
        kind: 'checkbox',
        key: 'showTrendLine',
        labelKey: 'analytics.dashboard.complexCharts.configuration.showTrendLine',
        default: false,
      },
    ],
    isConfigValid: (config) => {
      const xKind = getMetricKind(config.xAxis.metric);
      if (xKind !== null && !['categorical', 'numeric'].includes(xKind)) {
        return { valid: false, reason: 'Scatter chart X-axis must be categorical or numeric.' };
      }
      const yKind = getMetricKind(config.yAxis.metric);
      if (yKind !== 'numeric') {
        return { valid: false, reason: 'Scatter chart Y-axis must be a numeric metric.' };
      }
      return { valid: true };
    },
  },

  // ── Box Plot ─────────────────────────────────────────────────────────────
  {
    key: 'boxplot',
    labelKey: 'analytics.dashboard.complexCharts.configuration.types.boxplot',
    supportedXKinds: ['categorical', 'numeric'],
    supportedYKinds: ['numeric'],
    optionFields: [
      {
        kind: 'select',
        key: 'sortOrder',
        labelKey: 'analytics.dashboard.complexCharts.configuration.sortOrder',
        options: [
          {
            value: 'desc',
            labelKey: 'analytics.dashboard.complexCharts.configuration.sortDescending',
          },
          {
            value: 'asc',
            labelKey: 'analytics.dashboard.complexCharts.configuration.sortAscending',
          },
          {
            value: 'alpha',
            labelKey: 'analytics.dashboard.complexCharts.configuration.sortAlphabetical',
          },
        ],
        default: 'desc',
      },
      {
        kind: 'checkbox',
        key: 'showOutliers',
        labelKey: 'analytics.dashboard.complexCharts.configuration.showOutliers',
        default: true,
      },
    ],
    isConfigValid: (config) => {
      const yKind = getMetricKind(config.yAxis.metric);
      if (yKind !== 'numeric') {
        return { valid: false, reason: 'Box plot Y-axis must be a numeric metric.' };
      }
      return { valid: true };
    },
  },
];

export type ChartType = (typeof chartTypeRegistry)[number]['key'];
